// The office's record that a customer WAS told the new charge after a CPI rise (Session BAK). The renewal ledger finds out from
// the Sent folder; a rise told in a wording it does not recognise, or by phone or letter, reads "not disclosed" for ever. This
// is the person's own, audited statement, which the ledger then reads as disclosed while its date is inside the current cycle.
// Removable (RULE 1). Plain English: the office needs no knowledge of how the ledger decides.
import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type DisclosureMethod, type RenewalDisclosureLedger, type RenewalDisclosureRecordDto } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";

const METHODS: Array<{ value: DisclosureMethod; label: string }> = [
  { value: "email", label: "Email" },
  { value: "letter", label: "Letter" },
  { value: "phone", label: "Phone call" },
  { value: "in_person", label: "In person" },
  { value: "other", label: "Other" },
];
const METHOD_LABEL = Object.fromEntries(METHODS.map((m) => [m.value, m.label])) as Record<string, string>;

function ukDay(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export function RenewalDisclosurePanel({ contractId, findingOpen, onChanged }: { contractId: string; findingOpen: boolean; onChanged: () => void }) {
  const [records, setRecords] = useState<RenewalDisclosureRecordDto[] | null>(null);
  const [ledger, setLedger] = useState<RenewalDisclosureLedger | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [disclosedOn, setDisclosedOn] = useState("");
  const [method, setMethod] = useState<DisclosureMethod>("email");
  const [emailLink, setEmailLink] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.contracts.disclosureRecords(contractId);
      setRecords(r.records);
      setLedger(r.ledger);
    } catch {
      setRecords(null); // the panel is an aid: a failed read shows nothing rather than a false empty list
    }
  }, [contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (records === null) return null;
  if (!findingOpen && records.length === 0) return null;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.contracts.addDisclosureRecord(contractId, {
        disclosedOn,
        method,
        ...(emailLink.trim() ? { emailLink: emailLink.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setLedger(res.ledger);
      setOpen(false);
      setDisclosedOn("");
      setEmailLink("");
      setNote("");
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save that.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(recordId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.contracts.removeDisclosureRecord(contractId, recordId);
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not remove that.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Customer told about the price change">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg text-ele-strong">Customer told about the price change</h2>
        {!open && (
          <button type="button" onClick={() => setOpen(true)} className="rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:bg-ele-purple/10">
            Record that they were told
          </button>
        )}
      </div>
      <p className="mt-1 text-sm text-ele-body">
        The system looks in the sent mail for an email that states the old and the new charge. If the customer was told another way, or the email was worded so it was
        not recognised, record it here. It then counts as told, for the review it falls in.
      </p>

      {records.length > 0 && (
        <ul className="mt-3 space-y-2 text-sm text-ele-body">
          {records.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-ele border border-ele-purple-accent px-3 py-2">
              <span>
                <span className="text-ele-strong">{ukDay(r.disclosedOn)}</span> · {METHOD_LABEL[r.method] ?? r.method}
                {r.emailLink ? (
                  /^https:\/\//i.test(r.emailLink) ? (
                    <>
                      {" "}
                      · <a className="text-ele-blue underline" href={r.emailLink} target="_blank" rel="noreferrer">the email</a>
                    </>
                  ) : (
                    <> · email {r.emailLink}</>
                  )
                ) : null}
                {r.note ? <> · {r.note}</> : null}
                <span className="text-xs"> (recorded by {r.recordedByName})</span>
              </span>
              <ConfirmButton
                onConfirm={() => void remove(r.id)}
                busy={busy}
                busyLabel="Removing..."
                label="Remove"
                confirmLabel="Press again to remove"
                className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-danger hover:text-ele-danger"
              />
            </li>
          ))}
        </ul>
      )}

      {ledger && records.length > 0 && (
        <p className="mt-2 text-xs text-ele-body">
          {ledger.priceChangeDisclosedVia === "recorded"
            ? "The renewal check now reads this as told."
            : "These records are not in the current review, so the renewal check does not count them."}
        </p>
      )}

      {open && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="text-sm text-ele-body">
            Date they were told
            <input type="date" value={disclosedOn} onChange={(e) => setDisclosedOn(e.target.value)} className="mt-1 block w-full rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-ele-strong" />
          </label>
          <label className="text-sm text-ele-body">
            How
            <select value={method} onChange={(e) => setMethod(e.target.value as DisclosureMethod)} className="mt-1 block w-full rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-ele-strong">
              {METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-ele-body sm:col-span-2">
            Link to the email (optional)
            <input value={emailLink} onChange={(e) => setEmailLink(e.target.value)} placeholder="Paste the Gmail link" className="mt-1 block w-full rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-ele-strong" />
          </label>
          <label className="text-sm text-ele-body sm:col-span-2">
            How they acknowledged it (optional)
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} className="mt-1 block w-full rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-ele-strong" />
          </label>
          <div className="flex gap-3 sm:col-span-2">
            <button type="button" disabled={busy || !disclosedOn} onClick={() => void save()} className="rounded-ele bg-ele-purple px-4 py-2 font-menu text-sm text-white disabled:opacity-50">
              {busy ? "Saving..." : "Save"}
            </button>
            <button type="button" disabled={busy} onClick={() => setOpen(false)} className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-sm text-ele-body">
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}
